import React, { createContext, useContext, useEffect, useState } from "react";
import firebase from "firebase/compat/app";
import { auth } from "../firebase";
import { logLogin, logLogout } from "../services/activityLog";
import { getRoleByEmail, UserRole, isUserSuspended, canUseDebugView } from "../config/roles";

export type { UserRole };

export type AppUser = {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  displayName: string;
};

type AuthContextValue = {
  user: AppUser | null;
  isAuthenticated: boolean;
  isSealUser: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  debugViewDepartmentId: string | null;
  setDebugViewDepartmentId: (id: string | null) => void;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// ✅ 這段加在這裡：AuthContext 下方、mapFirebaseUser 上方
const TITLE_BY_EMAIL: Record<string, string> = {
  "kc@lg.app": "法務主管",
  "ops@lg.app": "營運長",
  "gm@lg.app": "總經理",
};

function normalizeDisplayName(email?: string | null, rawName?: string | null) {
  const e = (email || "").toLowerCase().trim();
  if (TITLE_BY_EMAIL[e]) return TITLE_BY_EMAIL[e];

  const n = (rawName || "").trim();
  if (n === "Ops") return "營運長";
  if (n === "Gm") return "總經理";
  if (n === "kc" || n === "KC") return "法務主管";

  return n;
}

function mapFirebaseUser(u: firebase.User | null): AppUser | null {
  if (!u) return null;
  const email = u.email || "";
  const role = getRoleByEmail(email);

  // ✅ 這裡改成統一正規化顯示名稱
  let displayName = normalizeDisplayName(email, u.displayName);

  if (!displayName && email) {
    displayName = normalizeDisplayName(email, email.split("@")[0]);
  }

  return {
    id: u.uid,
    name: displayName || "User",
    email: email,
    role: role,
    displayName: displayName || "User",
  };
}

async function fetchClientIpFromCloudflare(): Promise<string | undefined> {
  if (typeof window === "undefined") return undefined;
  try {
    const response = await fetch("/cdn-cgi/trace", { method: "GET" });
    if (!response.ok) return undefined;
    const text = await response.text();
    const ipLine = text.split("\n").find((line) => line.startsWith("ip="));
    const ip = ipLine?.split("=")[1]?.trim();
    if (ip) {
      window.localStorage.setItem("clientIp", ip);
      return ip;
    }
    return undefined;
  } catch (error) {
    return undefined;
  }
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [debugViewDepartmentId, _setDebugViewDepartmentId] = useState<string | null>(null);

  useEffect(() => {
    if (!auth) {
      setLoading(false);
      return;
    }

    fetchClientIpFromCloudflare();

    const unsub = auth.onAuthStateChanged((firebaseUser) => {
      if (firebaseUser) {
        if (process.env.NODE_ENV !== "production") {
          console.debug("[Auth] Firebase Auth Verified UID:", firebaseUser.uid);
        }
      }

      if (firebaseUser && isUserSuspended(firebaseUser.email)) {
        auth.signOut().then(() => {
          setUser(null);
          _setDebugViewDepartmentId(null);
          setLoading(false);
        });
        return;
      }

      setUser(mapFirebaseUser(firebaseUser));
      _setDebugViewDepartmentId(null);
      setLoading(false);
    });

    return () => unsub();
  }, []);

  const isAuthenticated = !!user;
  const isSealUser = user?.role === "sealUser";

  async function login(email: string, password: string) {
    if (!auth) throw new Error("Firebase Auth service is unavailable.");
    if (isUserSuspended(email)) throw new Error("此帳號目前停用，請聯繫法務或系統管理者。");
    try {
      const result = await auth.signInWithEmailAndPassword(email, password);
      if (result.user) {
        await fetchClientIpFromCloudflare();
        await logLogin({ uid: result.user.uid, email: result.user.email });
      }
    } catch (e) {
      throw e;
    }
  }

  async function logout() {
    if (!auth) return;
    if (auth.currentUser) {
      try {
        await logLogout({ uid: auth.currentUser.uid, email: auth.currentUser.email });
      } catch (e) {}
    }
    await auth.signOut();
    _setDebugViewDepartmentId(null);
  }

  const setDebugViewDepartmentId = (id: string | null) => {
    if (canUseDebugView(user?.email)) {
      _setDebugViewDepartmentId(id);
    } else {
      _setDebugViewDepartmentId(null);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#0f0f10",
          color: "#fff",
        }}
      >
        Loading...
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, isSealUser, login, logout, debugViewDepartmentId, setDebugViewDepartmentId }}>
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
 